import React, { forwardRef } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Grid } from '@mui/material';
import ReportPrintHeader from '../../components/ReportPrintHeader';

const AccountStatementPrint = forwardRef(({ reportData, fromDate, toDate }, ref) => {
  if (!reportData) return null;

  return (
    <Box ref={ref} sx={{ p: 4, bgcolor: '#fff', direction: 'rtl', minHeight: '100vh' }} className="print-container" dir="rtl">
      {/* Header */}
      <ReportPrintHeader 
        title="كشف حساب تفصيلي"
        subTitle={`${reportData.accountCode} - ${reportData.accountName}`}
        dateRange={`من ${new Date(fromDate).toLocaleDateString("en-GB")} إلى ${new Date(toDate).toLocaleDateString("en-GB")}`}
      />

      {/* Summary Section (Table Format for Print) */}
      <Box sx={{ mb: 3 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px", fontSize: "12px", direction: 'rtl' }}>
            <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: 'center' }}>رصيد افتتاحي</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: 'center' }}>إجمالي مدين</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: 'center' }}>إجمالي دائن</th>
                    <th style={{ border: "1px solid #ddd", padding: "8px", textAlign: 'center' }}>رصيد ختامي</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center", fontWeight: "bold" }}>{reportData.openingBalance?.toLocaleString()}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center", fontWeight: "bold" }}>{reportData.totalDebit?.toLocaleString()}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center", fontWeight: "bold" }}>{reportData.totalCredit?.toLocaleString()}</td>
                    <td style={{ border: "1px solid #ddd", padding: "8px", textAlign: "center", fontWeight: "bold" }}>{reportData.closingBalance?.toLocaleString()}</td>
                </tr>
            </tbody>
        </table>
      </Box>

      {/* Transactions Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #000' }} dir="rtl">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#eee', '& th': { borderBottom: '1px solid #000', fontWeight: 'bold', textAlign: 'center' } }}>
              <TableCell align="center" width="5%">#</TableCell>
              <TableCell align="center" width="12%">التاريخ</TableCell>
              <TableCell align="center" width="10%">نوع القيد</TableCell>
              <TableCell align="center" width="12%">مدين</TableCell>
              <TableCell align="center" width="12%">دائن</TableCell>
              <TableCell align="center" width="12%">الرصيد</TableCell>
              <TableCell align="right" width="37%">البيان</TableCell> {/* Moved to last as requested */}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Opening Balance Row */}
            <TableRow sx={{ bgcolor: "#fff3e0", '& td': { borderBottom: '1px solid #ccc', textAlign: 'center' } }}>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">{new Date(fromDate).toLocaleDateString("en-GB")}</TableCell>
              <TableCell align="center">-</TableCell>
              <TableCell align="center">
                {reportData.openingBalance > 0 ? reportData.openingBalance.toLocaleString() : "-"}
              </TableCell>
              <TableCell align="center">
                {reportData.openingBalance < 0 ? Math.abs(reportData.openingBalance).toLocaleString() : "-"}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                {reportData.openingBalance.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>رصيد افتتاحي</TableCell>
            </TableRow>

            {/* Data Rows */}
            {reportData.lines.map((row, index) => (
              <TableRow key={row.id} sx={{ '& td': { borderBottom: '1px solid #ccc', textAlign: 'center' } }}>
                <TableCell align="center">{row.entryNumber}</TableCell>
                <TableCell align="center">{new Date(row.date).toLocaleDateString("en-GB")}</TableCell>
                <TableCell align="center">{row.entryType}</TableCell>
                <TableCell align="center" sx={{ color: "#2e7d32", fontWeight: row.debit > 0 ? "bold" : "normal" }}>
                  {row.debit > 0 ? row.debit.toLocaleString() : "-"}
                </TableCell>
                <TableCell align="center" sx={{ color: "#c62828", fontWeight: row.credit > 0 ? "bold" : "normal" }}>
                  {row.credit > 0 ? row.credit.toLocaleString() : "-"}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold", color: "#1565c0" }}>
                  {row.balance.toLocaleString()}
                </TableCell>
                <TableCell align="right">{row.description}</TableCell>
              </TableRow>
            ))}

            {reportData.lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, fontSize: "1rem", color: "text.secondary" }}>
                  لا توجد حركات خلال الفترة المحددة
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <div className="report-footer">
        <Typography variant="caption">
            تمت الطباعة بواسطة نظام بروفي إي آر بي | ProfiERP System
        </Typography>
      </div>
      
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; }
          .report-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            text-align: center;
            font-size: 11px;
            color: #777;
            border-top: 1px solid #ddd;
            padding-top: 4px;
            background: white;
          }
        }
      `}</style>
    </Box>
  );
});

export default AccountStatementPrint;
